<template>
  <div class="header">
    <span class="title">{{ $t('viewOrders.download.selectable_fields_popup_title') }}</span>
    <span class="info-text">{{ $t('viewOrders.download.selectable_fields_popup_description') }}</span>
    <span class="field-count-text">{{
      $t('viewOrders.download.selectable_fields_popup_total_info', { total: fieldCount })
    }}</span>
  </div>

  <div class="select-all-section">
    <BackstageCheckbox v-model="selectAll" data-cy="selectable-fields-select-all">
      {{ $t('viewOrders.download.select_all') }}
    </BackstageCheckbox>
  </div>

  <div class="fields-list-container">
    <div class="fields-list">
      <BackstageCheckbox
        v-for="(field, key) in selectableFields"
        :key="key"
        v-model="field!.isSelected"
        :data-cy="`selectable-field-${key}`">
        {{ $t(`viewOrders.download.selectable_fields.${key}`) }}
      </BackstageCheckbox>
    </div>
  </div>

  <div class="footer">
    <BackstageButton :disabled="!selectedFields.length" full-width @click="handleExport">{{
      $t('viewOrders.download.export_button_label')
    }}</BackstageButton>
  </div>
</template>

<script setup lang="ts">
import { useSelectableFields } from '@/composables/useSelectableFields';
import { useOrderDownloadStore } from '@/stores/order-download';
import type { LineSelectableFieldsKeys } from '@onrunning/backstage-models/dist/orders/line-selectable-fields.model';
import { computed } from 'vue';
import BackstageButton from '../common/BackstageButton.vue';
import BackstageCheckbox from '../common/BackstageCheckbox.vue';

const { selectableFields } = useSelectableFields();
const orderDownloadStore = useOrderDownloadStore();

const emit = defineEmits(['export']);

const selectAll = computed({
  get: () => Object.values(selectableFields.value).every((field) => field.isSelected),
  set: (value) => {
    const updatedFields: typeof orderDownloadStore.selectableFields = {};

    for (const key in selectableFields.value) {
      const field = selectableFields.value[key as LineSelectableFieldsKeys];

      if (field) {
        updatedFields[key as LineSelectableFieldsKeys] = {
          ...field,
          isSelected: value,
        };
      }
    }

    orderDownloadStore.setSelectableFields(updatedFields);
  },
});

const selectedFields = computed(() =>
  Object.values(selectableFields.value)
    .filter((field) => field.isSelected)
    .map((field) => field.key),
);

const fieldCount = computed(() => Object.keys(selectableFields.value).length);

function handleExport() {
  emit('export', selectedFields.value);
}
</script>

<style scoped>
.header {
  display: flex;
  flex-direction: column;
  text-align: left;
  margin-bottom: 24px;
  margin-top: 32px;

  .title {
    font-size: 24px;
    font-weight: 500;
    margin-bottom: 16px;
  }

  .info-text {
    font-weight: 700;
    margin-bottom: 0.5rem;
  }

  .field-count-text {
    color: #999999;
  }
}

.select-all-section {
  border-bottom: 1px solid #e5e5e5;
  padding-bottom: 24px;
  margin-bottom: 24px;
}

.fields-list-container {
  flex-grow: 1;
  max-height: 400px;
  overflow-y: scroll;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: #e5e5e5 #f7f7f7;
}

.fields-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.footer {
  margin-top: 3rem;
}

/* WebKit browsers (Chrome, Safari, Edge) */
.fields-list-container::-webkit-scrollbar {
  width: 8px;
}
.fields-list-container::-webkit-scrollbar-track {
  background-color: #f7f7f7;
  border-radius: 10px;
}
.fields-list-container::-webkit-scrollbar-thumb {
  background-color: #e5e5e5;
  border-radius: 10px;
}

@media (max-width: 1512px) {
  .fields-list-container {
    max-height: 300px;
  }
}

@media (max-width: 768px) {
  .header {
    margin-bottom: 0.75rem;

    .title {
      font-size: 20px;
    }

    .info-text {
      font-size: 14px;
    }

    .field-count-text {
      font-size: 14px;
    }
  }

  .fields-list-container {
    max-height: 300px;
  }

  .footer {
    margin-top: 2rem;
  }
}

@media (max-width: 480px) {
  .header {
    margin-bottom: 0.5rem;

    .title {
      font-size: 18px;
      margin-bottom: 0.25rem;
    }

    .info-text {
      font-size: 13px;
      margin-bottom: 0.25rem;
    }

    .field-count-text {
      font-size: 13px;
    }
  }

  .select-all-section {
    padding-bottom: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .fields-list-container {
    max-height: 250px;
  }

  .fields-list {
    gap: 0.5rem;
  }

  .footer {
    margin-top: 1.5rem;
  }
}
</style>
